import { useEffect, useMemo, useState } from "react";
import { Check, Phone, Search, Info } from "lucide-react";
import { money, unitPrice, slug } from "@/lib/api";
import { BoatConfigurator } from "@/components/BoatConfigurator";
import { SampleLink } from "@/components/SampleLink";
import { STANDARD_FEATURES } from "@/components/StandardFeatures";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DetailsDialog = ({ product, onClose, onSelect, selected }) => (
  <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="package-details-dialog">
      {product && (
        <>
          <DialogHeader>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#1B66C9]">{product.brand} · {product.series}</p>
            <DialogTitle className="font-display text-xl">{product.name}</DialogTitle>
            <DialogDescription>
              {product.description} <span className="font-bold text-[#1B66C9]">{money(product.price)}</span>
            </DialogDescription>
          </DialogHeader>
          {product.image_url && (
            <img src={product.image_url} alt={product.name} className="max-h-64 w-full rounded-md object-contain bg-secondary/40" />
          )}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {product.details.map((line, i) => {
              const [head, rest] = line.split(/:\s(.+)/);
              return (
                <section key={i} data-testid={`details-section-${i}`}>
                  <h4 className="text-xs font-black uppercase tracking-[0.15em]">{head}</h4>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {(rest || "").split(/,\s(?![^()]*\))/).map((item) => (
                      <li key={item} className="flex gap-2">
                        <Check size={13} className="mt-1 flex-shrink-0 text-[#1B66C9]" />
                        <span className="first-letter:uppercase">{item.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
          <button
            data-testid="details-select-button"
            onClick={() => {
              if (!selected) onSelect(product);
              onClose();
            }}
            className="mt-2 h-12 w-full rounded-md bg-[#1B66C9] font-bold text-white hover:bg-[#144E9C]"
          >
            {selected ? "Selected — close" : `Select this package · ${money(product.price)}`}
          </button>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const ModelCard = ({ product, selected, price, onClick, onDetails, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`group relative flex flex-col items-start rounded-lg border bg-card p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
      selected ? "border-2 border-[#1B66C9] shadow-md" : "border-border"
    }`}
  >
    {selected && (
      <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-[#1B66C9] text-white">
        <Check size={13} strokeWidth={3} />
      </span>
    )}
    <span className="font-display text-base font-bold leading-tight pr-7">{product.name}</span>
    {product.description && (
      <span className={`mt-1 text-xs text-muted-foreground ${selected ? "" : "line-clamp-3"}`} title={product.description}>
        {product.description}
      </span>
    )}
    <span className="mt-3 flex w-full flex-wrap items-end justify-between gap-2">
      <span className="font-display text-lg font-black text-[#1B66C9]">
        {money(price)}
        {Number(product.shipping) > 0 && (
          <span className="block whitespace-nowrap font-sans text-[10px] font-bold text-muted-foreground" data-testid={`${testId}-shipping`}>
            + {money(product.shipping)} shipping
          </span>
        )}
      </span>
      {product.details?.length > 0 && (
        <span
          role="button"
          data-testid={`${testId}-details`}
          onClick={(e) => {
            e.stopPropagation();
            onDetails(product);
          }}
          className="flex items-center gap-1 whitespace-nowrap rounded-full border border-border px-2.5 py-1 text-[11px] font-bold hover:border-[#1B66C9] hover:text-[#1B66C9]"
        >
          <Info size={12} /> Full specs
        </span>
      )}
    </span>
    {product.note && (
      <span
        data-testid={`${testId}-note`}
        className="mt-2 flex items-start gap-1.5 rounded-md border border-[#1B66C9]/30 bg-[#1B66C9]/10 px-2.5 py-1.5 text-[11px] font-bold leading-snug text-[#144E9C]"
      >
        <Info size={12} className="mt-0.5 flex-shrink-0" /> {product.note}
      </span>
    )}
    {product.category === "boat" && STANDARD_FEATURES[product.brand] && (
      <span className="mt-2 block text-[11px] leading-snug text-muted-foreground" data-testid={`${testId}-std-note`}>
        Standard features included · configure options below
      </span>
    )}
    {product.samples_enabled && (
      <span className="mt-2 flex w-full">
        <SampleLink product={product} testId={testId} className="max-w-full !whitespace-normal text-left" />
      </span>
    )}
  </button>
);

// Gator Trax GT hulls come in 50+ sizes: split the tab by hull length (GT Series · 17 ft) so each page stays short.
const seriesTab = (b) => {
  if (b.brand === "Gator Trax" && b.series === "GT Series" && b.hull_length) {
    const hull = /hybrid/i.test(b.name) ? "Hybrid" : "Standard";
    return `GT Series · ${b.hull_length} ft ${hull}`;
  }
  return b.series || "";
};

// Keep short lists on one row (no lone card underneath) so the configure panel stays in view.
const gridCols = (n) => ({ 1: "xl:grid-cols-1", 2: "xl:grid-cols-2", 3: "xl:grid-cols-3", 4: "xl:grid-cols-4", 5: "xl:grid-cols-5", 6: "xl:grid-cols-3", 9: "xl:grid-cols-5", 10: "xl:grid-cols-5" }[n] || "xl:grid-cols-4");

export const BoatStep = ({ brands, boats, selection, optionSets, onSelect, onVariant, onVariantQty, onAddon, onBrandChange, noun = "hull" }) => {
  const memKey = `fdm_step_${noun}`;
  const remembered = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem(memKey) || "{}");
    } catch (e) {
      return {};
    }
  }, [memKey]);
  const [brand, setBrand] = useState(selection?.product.brand || remembered.brand || "");
  const [series, setSeries] = useState(selection?.product ? seriesTab(selection.product) : remembered.series || "");
  const [query, setQuery] = useState("");
  const [detailsProduct, setDetailsProduct] = useState(null);

  const activeBrand = brand || selection?.product.brand || "";  // no auto-pick: wait for the customer to choose
  useEffect(() => {
    onBrandChange?.(activeBrand);
  }, [activeBrand]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    sessionStorage.setItem(memKey, JSON.stringify({ brand: activeBrand, series }));
  }, [memKey, activeBrand, series]);
  const brandBoats = useMemo(() => boats.filter((b) => b.brand === activeBrand), [boats, activeBrand]);
  const seriesList = useMemo(() => {
    const tabs = [...new Set(brandBoats.map(seriesTab).filter(Boolean))];
    const key = (s) => (s.startsWith("GT Series · ") ? parseInt(s.split("· ")[1], 10) * 10 + (s.endsWith("Hybrid") ? 1 : 0) : 9999);
    return tabs.sort((a, b) => key(a) - key(b));
  }, [brandBoats]);
  const activeSeries = seriesList.includes(series) ? series : seriesList[0] || "";
  const models = useMemo(
    () =>
      brandBoats
        .filter((b) => !activeSeries || seriesTab(b) === activeSeries)
        .filter((b) => !query || b.name.toLowerCase().includes(query.toLowerCase())),
    [brandBoats, activeSeries, query]
  );

  return (
    <div>
      <DetailsDialog
        product={detailsProduct}
        onClose={() => setDetailsProduct(null)}
        onSelect={onSelect}
        selected={!!detailsProduct && selection?.product.id === detailsProduct.id}
      />
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">Brand</p>
      <div className={`mt-2 grid grid-cols-1 gap-3 ${brands.length === 4 ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}>
        {brands.map((b) => {
          const active = b.key === activeBrand;
          const count = boats.filter((p) => p.brand === b.key).length;
          return (
            <button
              key={b.key}
              data-testid={`brand-${slug(b.key)}`}
              onClick={() => {
                setBrand(b.key);
                setSeries("");
                setQuery("");
              }}
              className={`relative flex h-32 items-end overflow-hidden rounded-lg border bg-white text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:bg-secondary ${
                active ? "border-2 border-[#1B66C9] shadow-md" : "border-border"
              }`}
            >
              {b.image_url && (
                <img
                  src={b.image_url}
                  alt={`${b.key} ${noun}`}
                  className={`absolute inset-y-0 right-0 h-full object-contain object-right p-1.5 ${noun === "hull" ? "w-[72%]" : "w-[60%]"}`}
                />
              )}
              <span className="absolute inset-0 bg-gradient-to-r from-white via-white via-32% to-transparent to-58% dark:from-secondary dark:via-secondary" />
              <span className="relative max-w-[55%] p-3">
                <span className="block whitespace-nowrap font-display text-lg font-black leading-tight">{b.key}</span>
                <span className="block text-[11px] text-muted-foreground">
                  {count ? `${count} model${count > 1 ? "s" : ""}` : b.tagline || "Call for pricing"}
                </span>
              </span>
              {active && (
                <span className="absolute left-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-[#1B66C9] text-white">
                  <Check size={13} strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {!activeBrand ? (
        <p className="mt-6 rounded-lg border border-dashed border-border bg-secondary/40 p-6 text-center text-sm text-muted-foreground" data-testid="brand-prompt">
          Pick a brand above to see the {noun}s we offer.
        </p>
      ) : brandBoats.length === 0 ? (
        <div
          data-testid="brand-empty-state"
          className="mt-6 rounded-lg border border-dashed border-border bg-secondary/40 p-6 text-center"
        >
          <p className="font-display text-lg font-bold">{activeBrand} models are quoted by phone</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Call or text us and we'll price your {activeBrand} {noun}, or keep building and mention it in the
            comments on the review step.
          </p>
          <a
            href="tel:3867477652"
            data-testid="brand-call-link"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#1E231F] px-5 py-2.5 text-sm font-bold text-white hover:bg-black"
          >
            <Phone size={15} /> 386-747-7652
          </a>
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1.5">
              {seriesList.map((s) => (
                <button
                  key={s}
                  data-testid={`series-${slug(s)}`}
                  onClick={() => setSeries(s)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                    s === activeSeries ? "bg-[#1E231F] text-white dark:bg-white dark:text-black" : "bg-secondary hover:bg-secondary/70"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            {brandBoats.length > 8 && (
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="model-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={noun === "hull" ? "Filter by size, e.g. 1754" : "Filter, e.g. 40 EFI"}
                  className="h-9 w-56 rounded-md border border-border bg-background pl-8 pr-3 text-sm"
                />
              </div>
            )}
          </div>

          <div className={`mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 ${gridCols(models.length)}`} data-testid="model-grid">
            {models.map((p, i) => {
              const sel = selection?.product.id === p.id ? selection : null;
              return (
                <ModelCard
                  key={p.id}
                  product={p}
                  selected={!!sel}
                  price={sel ? unitPrice(p, sel.variants, sel.addons, optionSets) : p.price}
                  onClick={() => onSelect(p)}
                  onDetails={setDetailsProduct}
                  testId={`model-${slug(activeSeries)}-${i}`}
                />
              );
            })}
            {models.length === 0 && (
              <p className="col-span-full py-8 text-center text-sm text-muted-foreground">No models match.</p>
            )}
          </div>
        </>
      )}

      {selection && (
        <BoatConfigurator
          product={selection.product}
          selection={selection}
          optionSets={optionSets}
          onVariant={onVariant}
          onVariantQty={onVariantQty}
          onAddon={onAddon}
        />
      )}
    </div>
  );
};
